import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import VisibilitySensor from "react-visibility-sensor";
import * as icons from "../../config/Icons";
import styles from "./index.module.scss";

export default function BarChartStacked({
  size = "medium",
  title,
  data,
  colors,
  fontColors,
  legends,
  labelX,
  labelY,
  showTotal,
  totalSuffix,
  hasFilter,
  maxValue,
  responseKeyPrimary = "primary",
  responseKeySecondary = "secondary",
  animDuration = 1000,
  animPredelay = 0,
  variant,
  countryIcon = false,
  average = false,
  debug = false,
  showLabel = true,
  ...props
}) {
  const { t } = useTranslation();
  const [showValues, setShowValues] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setShowValues(true);
      }, animPredelay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // only animate on component visibility
  const onVisibilityChange = isVisible => {
    if (isVisible) {
      setIsVisible(true);
    }
  };

  return (
    <div className={styles.container}>
      {title && (
        <div
          className={ClassNames(styles.title, hasFilter && styles.hasFilter)}
        >
          <strong dangerouslySetInnerHTML={{ __html: title }}></strong>
        </div>
      )}
      <div className={styles.barCharts}>
        {!_.isEmpty(data) && (
          <VisibilitySensor
            partialVisibility={true}
            onChange={onVisibilityChange}
          >
            <ul className={styles.barChartsList}>
              {variant === "left" && (labelX || labelY) && (
                <li className={styles.barChartsHeader}>
                  <div className={styles.labelX}>{labelX}</div>
                  <div className={[styles.labelY, styles.show].join(" ")}>
                    {labelY}
                  </div>
                </li>
              )}

              {average && (
                <li
                  className={ClassNames(
                    styles.barChartsItem,
                    variant === "left" && styles.leftVariant
                  )}
                >
                  <div className={styles.labelX}>{average.name}</div>
                  <div className={[styles.labelY].join(" ")}>
                    <div
                      style={{ transition: `${animDuration}ms width` }}
                      className={[
                        styles.labelYInner,
                        showValues ? styles.show : "",
                      ].join(" ")}
                    >
                      <div
                        className={ClassNames(
                          styles.bar,
                          size === "small" && styles.small
                        )}
                        style={{
                          width: `${(average.value / maxValue) * 100}%`,
                          backgroundColor: `${props.averageColors}`,
                          minWidth: "5px",
                        }}
                      >
                        <span
                          style={{
                            transitionDuration: `${animDuration}ms`,
                            color: `#212121`,
                          }}
                        >
                          {average.value}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              )}

              {!_.isEmpty(data) &&
                data.map((item, index) => {
                  const flagIcon =
                    item && item.name && item.name.trim() + "FlagIcon";
                  return (
                    <li
                      className={ClassNames(
                        styles.barChartsItem,
                        variant === "left" && styles.leftVariant
                      )}
                      key={index}
                    >
                      {showLabel && (
                        <div className={styles.labelX}>
                          {countryIcon && (
                            <img src={icons[flagIcon]} alt={item.name} />
                          )}
                          {item.name}
                        </div>
                      )}
                      <div className={[styles.labelY].join(" ")}>
                        <div
                          style={{
                            transition: `${animDuration}ms width`,
                            maxWidth: "95%",
                          }}
                          className={[
                            styles.labelYInner,
                            showValues ? styles.show : "",
                          ].join(" ")}
                        >
                          {Object.keys(item).map((list, i) => (
                            <>
                              {list !== "name" && (
                                <div
                                  key={i}
                                  className={ClassNames(
                                    styles.bar,
                                    size === "small" && styles.small
                                  )}
                                  style={{
                                    width: `${parseInt(maxValue) !== 0
                                        ? (item[list] / parseInt(maxValue)) *
                                        100
                                        : 0
                                      }%`,
                                    backgroundColor: `${colors[i - 1]}`,
                                    minWidth: "5px",
                                  }}
                                >
                                  <span
                                    style={{
                                      transitionDuration: `${animDuration}ms`,
                                      color: `${fontColors[i]}`,
                                    }}
                                  >
                                    {item[list]}
                                  </span>
                                </div>
                              )}
                            </>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </VisibilitySensor>
        )}
        {_.isEmpty(data) && (
          <div className={styles.nodata}>{t("dashboard.noDataYet")}</div>
        )}
      </div>

      {legends && (
        <div className={styles.footer}>
          <div className={styles.legends}>
            <ul className={styles.legendsList}>
              {legends.map((legend, index) => {
                return (
                  <li className={styles.legendItem} key={legend}>
                    <div
                      className={styles.legendColor}
                      style={{ backgroundColor: `${colors[index]}` }}
                    ></div>
                    <span>{legend}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
